import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { HttpError, HttpService } from 'src/toolkit';
import { TTableSort, TableSort } from 'src/components/table/thead/col';

import { useAppSelector } from '../../hooks';
import { ApplicationUserRelationListSortCol, TApplicationUserRelationList, TApplicationUserRelationListResponse, TApplicationUserRelationListQuery, TApplicationUserRelationListSortCol, adaptApplicationUserRelationFromListResponse, adaptApplicationUserRelationListQueryToRequest } from '../../types';
import { BACKEND_ADMIN_APPLICATION_USER_RELATION } from '../../backend-paths';


export const applicationUserRelationListGetApi = createAsyncThunk(
	BACKEND_ADMIN_APPLICATION_USER_RELATION,
	(payload: TApplicationUserRelationListQuery | undefined, thunkApi) => {
		const params = adaptApplicationUserRelationListQueryToRequest(payload);

		return HttpService.get<TApplicationUserRelationListResponse>(BACKEND_ADMIN_APPLICATION_USER_RELATION, { params }).then(result => {
			return adaptApplicationUserRelationFromListResponse(result.data);
		}).catch((error: HttpError) => {
			return thunkApi.rejectWithValue({ ...error });
		});
	},
);

type TInitialState = {
	isLoading: boolean;
	list: TApplicationUserRelationList;
	query: TApplicationUserRelationListQuery;
	error?: HttpError;
};

const initialState: TInitialState = {
	isLoading: false,
	list: {
		items: [],
		count: 0,
	},
	query: {
		limit: 10,
		offset: 0,
		search: undefined,
		sort: TableSort.ASC,
		sortCol: ApplicationUserRelationListSortCol.CREATED_AT,
	},
	error: undefined,
};

export const applicationUserRelationListSlice = createSlice({
	name: 'applicationUserRelationListSlice',
	initialState,
	reducers: {
		setApplicationUserRelationListQueryLimit(state, action: PayloadAction<number>) {
			state.query.limit = action.payload;
		},

		setApplicationUserRelationListQueryOffset(state, action: PayloadAction<number>) {
			state.query.offset = action.payload;
		},

		setApplicationUserRelationListQuerySearch(state, action: PayloadAction<string>) {
			const search = action.payload || undefined;
			const isSearch = search && search.length > 0;

			if (isSearch && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.search = search;
		},

		setApplicationUserRelationListQuerySort(state, action: PayloadAction<TTableSort>) {
			state.query.sort = action.payload;
		},

		setApplicationUserRelationListQuerySortCol(state, action: PayloadAction<TApplicationUserRelationListSortCol>) {
			state.query.sortCol = action.payload;
		},

		clearApplicationUserRelationList(state) {
			return { ...initialState, list: state.list, query: state.query };
		},
	},

	extraReducers: {
		[applicationUserRelationListGetApi.pending.type]: (state) => {
			state.isLoading = true;
			state.error = undefined;
		},

		[applicationUserRelationListGetApi.fulfilled.type]: (state, action: PayloadAction<TApplicationUserRelationList>) => {
			state.isLoading = false;
			state.list = action.payload;
			state.error = undefined;
		},

		[applicationUserRelationListGetApi.rejected.type]: (state, action: PayloadAction<HttpError>) => {
			state.isLoading = false;
			state.error = action.payload;
		},
	},
});

export const useApplicationUserRelationListSelector = () => useAppSelector(state => state[applicationUserRelationListSlice.name]);

export const { clearApplicationUserRelationList, setApplicationUserRelationListQueryLimit, setApplicationUserRelationListQueryOffset, setApplicationUserRelationListQuerySort, setApplicationUserRelationListQuerySortCol, setApplicationUserRelationListQuerySearch } = applicationUserRelationListSlice.actions;
